/**
 * Client helpers for the 1:1 call ring routes on the calls worker.
 *
 * Each mints a fresh service-auth token for the current user (via
 * resolveCallSession) and POSTs to the worker, which pushes the corresponding
 * event over the target's presence socket. The worker returns the authoritative
 * pair-room slug so the caller never has to compute it.
 */

import {type AtpAgent} from '@atproto/api'

import {callFetchJson,makeCallFetch} from '#/screens/Call/cfcalls/callFetch'
import {resolveCallSession} from '#/screens/Call/cfcalls/callToken'

interface RingResult {
  ok: boolean
  roomSlug: string
  delivered: number
}

function callFetchFor(agent: AtpAgent, handle?: string) {
  return makeCallFetch(async () => {
    const session = await resolveCallSession({agent, hasSession: true, handle})
    return session.getToken()
  })
}

function jsonBody(toDid: string): RequestInit {
  return {
    method: 'POST',
    headers: {'content-type': 'application/json'},
    body: JSON.stringify({toDid}),
  }
}

/**
 * Rings a callee. Returns the pair-room slug the caller should navigate into.
 */
export async function ringUser(
  agent: AtpAgent,
  toDid: string,
  handle?: string,
): Promise<RingResult> {
  return callFetchJson<RingResult>(
    callFetchFor(agent, handle),
    '/call/ring',
    jsonBody(toDid),
  )
}

/** Tells the callee the caller gave up (dismiss their incoming-call UI). */
export async function cancelRing(
  agent: AtpAgent,
  toDid: string,
  handle?: string,
): Promise<void> {
  await callFetchJson(callFetchFor(agent, handle), '/call/ring-cancel', jsonBody(toDid))
}

/** Tells the caller the callee declined (stop their "Ringing…" UI). */
export async function declineRing(
  agent: AtpAgent,
  toDid: string,
  handle?: string,
): Promise<void> {
  await callFetchJson(
    callFetchFor(agent, handle),
    '/call/ring-decline',
    jsonBody(toDid),
  )
}
